import { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { Export } from '@phosphor-icons/react/dist/ssr/Export';
import { CaretDoubleDown } from '@phosphor-icons/react/dist/ssr/CaretDoubleDown';
import { useNavigation } from '../contexts/NavigationContext';
import { SHADOWS } from '../styles/UISystem';
import { logEvent, getProfileUsername } from '../services/analytics/eventLogger';
import { AddToHomeScreenIcon } from '../components/UISystem/Icons/AddToHomeScreenIcon';

/**
 * ProfileSignupSuccessScreen
 *
 * Shown after profile creation (both first-time and anon-to-profile conversion).
 * Guides new Profile users to install the PWA.
 *
 * iOS: Shows manual instructions (Share → Add to Home Screen).
 * Android: Shows "Install Royale" button if beforeinstallprompt is available,
 *   falls back to iOS instructions after 500ms if not.
 *
 * X button → navigates to connections list (does NOT set install_prompt_dismissed).
 */

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);

const getPlatform = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'other';
};

const ProfileSignupSuccessScreen = () => {
  const { navigateTo } = useNavigation();
  const [platform] = useState(getPlatform);
  const [installComplete, setInstallComplete] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  // Track whether we've reloaded for a fresh install context
  const [ready, setReady] = useState(false);
  // On Android, wait briefly for beforeinstallprompt before showing content
  const [androidReady, setAndroidReady] = useState(platform !== 'android');

  // On first mount: reload for fresh beforeinstallprompt + proper PWA install context.
  // On second mount (after reload): capture the event and render.
  // Skip reload for pending profile conversions — the reload loses DemoGate context
  // (chatRoomId is no longer in the URL), causing DemoGate to kick the Anon out.
  useEffect(() => {
    const fromConversion = sessionStorage.getItem('from_pending_conversion') === 'true';
    if (fromConversion) {
      sessionStorage.removeItem('from_pending_conversion');
      setReady(true);
      return;
    }
    if (sessionStorage.getItem('signup_success_reloaded') === 'true') {
      // We're back from reload — ready to show content
      sessionStorage.removeItem('signup_success_reloaded');
      setReady(true);
    } else {
      // First visit — need a fresh page load
      sessionStorage.setItem('signup_success_reloaded', 'true');
      window.location.reload();
    }
  }, []);

  // Capture beforeinstallprompt after reload (Android)
  useEffect(() => {
    if (!ready) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setAndroidReady(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Give 500ms for the event to fire, then show content anyway (fallback to manual instructions)
    const timer = platform === 'android' ? setTimeout(() => setAndroidReady(true), 500) : undefined;
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (timer) clearTimeout(timer);
    };
  }, [ready, platform]);

  useEffect(() => {
    if (ready) {
      logEvent('install_prompt_shown', { platform, profileUsername: getProfileUsername() });
    }
  }, [ready, platform]);

  const handleDismiss = () => {
    logEvent('install_prompt_dismissed', { platform, profileUsername: getProfileUsername() });
    navigateTo('profile-connections-list');
  };

  const handleNativeInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      setDeferredPrompt(null);
    }
    logEvent('install_prompt_accepted', { platform, profileUsername: getProfileUsername() });
    localStorage.setItem('install_prompt_dismissed', 'true');
    setInstallComplete(true);
  };

  // Shared white card style for instruction steps
  const cardStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '14px 24px',
    width: '100%',
    maxWidth: '320px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  // App icon + label used in final step
  const AppIconBlock = ({ label }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '36px',
        gap: '4px',
      }}
    >
      <span style={{ fontSize: '15px', color: '#999' }}>{label}</span>
      <img
        src={`${process.env.PUBLIC_URL}/web-app-manifest-192x192.png`}
        alt="Royale"
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '14px',
          boxShadow: SHADOWS.TIER2,
          marginTop: '30px',
        }}
      />
      <span style={{ fontSize: '13px', color: '#999' }}>Royale</span>
    </div>
  );

  // iOS: Share → Add to Home Screen (two-step)
  const IOSInstructions = () => (
    <>
      <div
        style={{
          fontSize: '22px',
          fontWeight: '300',
          color: '#48C0FC',
          textAlign: 'center',
          marginBottom: '24px',
        }}
      >
        Now get the app
      </div>

      {/* Tap browser menu ••• */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '17px',
          color: '#999',
          marginBottom: '20px',
        }}
      >
        <span>Tap browser menu</span>
        <span style={{ fontSize: '20px', fontWeight: '900', letterSpacing: '3px', lineHeight: 1 }}>
          &bull;&bull;&bull;
        </span>
      </div>

      <div style={cardStyle}>
        <Export size={22} color="#333" weight="regular" />
        <span style={{ fontSize: '19px', color: '#333', fontWeight: '600' }}>Share</span>
      </div>

      <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'center' }}>
        <CaretDoubleDown size={24} color="#999" weight="bold" />
      </div>

      <div style={cardStyle}>
        <AddToHomeScreenIcon size={22} />
        <span style={{ fontSize: '19px', color: '#333', fontWeight: '600' }}>
          Add to Home Screen
        </span>
      </div>

      <AppIconBlock label="Wait for download, and open" />
    </>
  );

  // Android: manual instructions when beforeinstallprompt not available (⋮ → Add to Home Screen)
  const AndroidInstructions = () => (
    <>
      <div
        style={{
          fontSize: '22px',
          fontWeight: '300',
          color: '#48C0FC',
          textAlign: 'center',
          marginBottom: '24px',
        }}
      >
        Now get the app
      </div>

      {/* Tap browser menu ⋮ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '17px',
          color: '#999',
          marginBottom: '20px',
        }}
      >
        <span>Tap browser menu</span>
        <span style={{ fontSize: '20px', fontWeight: '900', letterSpacing: '0', lineHeight: 1 }}>
          &#8942;
        </span>
      </div>

      <div style={cardStyle}>
        <AddToHomeScreenIcon size={22} />
        <span style={{ fontSize: '19px', color: '#333', fontWeight: '600' }}>
          Add to Home Screen
        </span>
      </div>

      <AppIconBlock label="Wait for download, and open" />
    </>
  );

  // Android install button content
  const AndroidInstallContent = () => (
    <>
      <div
        style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#000',
          textAlign: 'center',
          marginBottom: '24px',
        }}
      >
        Now get the app
      </div>

      <div
        onClick={handleNativeInstall}
        style={{
          backgroundColor: '#48C0FC',
          color: '#fff',
          fontSize: '17px',
          fontWeight: '600',
          padding: '14px 40px',
          borderRadius: '28px',
          cursor: 'pointer',
        }}
      >
        Install Royale
      </div>
    </>
  );

  // Android post-install content
  const AndroidPostInstall = () => (
    <>
      <img
        src={`${process.env.PUBLIC_URL}/web-app-manifest-192x192.png`}
        alt="Royale"
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '14px',
          boxShadow: SHADOWS.TIER2,
          marginBottom: '4px',
        }}
      />
      <span style={{ fontSize: '13px', color: '#999', marginBottom: '24px' }}>Royale</span>

      <span style={{ fontSize: '17px', color: '#999', textAlign: 'center' }}>
        Go to your home screen
        <br />
        and open the app
      </span>
    </>
  );

  // Determine what to show
  const renderContent = () => {
    if (!ready || !androidReady) return null;
    if (platform === 'android') {
      if (installComplete) return <AndroidPostInstall />;
      if (deferredPrompt) return <AndroidInstallContent />;
      // No native prompt available — show manual instructions
      return <AndroidInstructions />;
    }
    // iOS / other: manual instructions
    return <IOSInstructions />;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#F5F5F5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* X button — top left */}
      <div
        onClick={handleDismiss}
        style={{
          position: 'fixed',
          top: 'calc(16px + env(safe-area-inset-top))',
          left: '16px',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FFFFFF',
          boxShadow: SHADOWS.TIER2,
          cursor: 'pointer',
          zIndex: 10,
        }}
      >
        <X size={22} weight="regular" color="#555555" />
      </div>

      {/* Success header */}
      <div
        style={{
          marginTop: 'calc(80px + env(safe-area-inset-top))',
          fontSize: '28px',
          fontWeight: '700',
          color: '#000',
          textAlign: 'center',
          marginBottom: '32px',
        }}
      >
        Success!
      </div>

      {/* Platform-specific content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 32px',
          width: '100%',
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default ProfileSignupSuccessScreen;
